import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useApiRoutes } from '@/lib/utils/useApiRoutes'
import type { Notification } from '@/lib/types/Models/Notification'

/**
 * Notifications inbox store.
 *
 * Owns:
 *   - Unread count for the header bell badge (polled every 60s
 *     while the document is visible).
 *   - Polling lifecycle (start / stop) — page-driven; bell starts
 *     on app boot, inbox view also polls list refreshes.
 *
 * Does NOT own:
 *   - The paginated list (lives in `NotificationsView` local state
 *     because filters / sort / pagination are URL-driven and tied
 *     to the view's lifecycle).
 *   - The selected notification (also view-local).
 *
 * Future SSE migration: replace `startPolling` with a subscription
 * to `/api/notifications/stream`; store applies push updates
 * directly. Public surface (`unreadCount`, `refreshUnreadCount`)
 * stays the same.
 */

export const useNotificationsStore = defineStore('notifications', () => {
  const routes = useApiRoutes()

  /** Unread count for the header bell. Cheap; single COUNT query. */
  const unreadCount = ref<number>(0)
  /** True while the bell-count fetch is in flight (rare flash). */
  const loadingUnreadCount = ref<boolean>(false)

  /**
   * Refresh the unread count from the BE. Used by the polling
   * loop AND by per-action callbacks (after mark-read / mark-unread
   * the inbox view calls this to keep the bell in sync).
   */
  const refreshUnreadCount = async (): Promise<void> => {
    loadingUnreadCount.value = true
    try {
      const resp = await routes.NOTIFICATIONS_UNREAD_COUNT
        .get<{ unread_count: number }>()
        .notifyOnError(false)
        .send()
      unreadCount.value = resp?.unread_count ?? 0
    } catch {
      // Quiet — operator doesn't need a toast every time the bell
      // poll fails (network blip, auth expiring, etc.). The badge
      // shows the last known count.
    } finally {
      loadingUnreadCount.value = false
    }
  }

  // ── Polling lifecycle ────────────────────────────────────────
  // Bell polls every 60s while tab visible. Pauses when hidden
  // (page-visibility API) to avoid burning battery + server load
  // on background tabs.

  let pollTimer: ReturnType<typeof setInterval> | null = null
  const POLL_INTERVAL_MS = 60_000

  const startPolling = (): void => {
    if (pollTimer !== null) return
    void refreshUnreadCount()
    pollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      void refreshUnreadCount()
    }, POLL_INTERVAL_MS)
  }

  const stopPolling = (): void => {
    if (pollTimer !== null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  // ── Per-row mutations (called from list / detail surfaces) ───
  // These mutate the BE + refresh the unread count. The list view
  // also patches its local state optimistically so the UI doesn't
  // wait for the count round-trip.

  const markRead = async (id: number): Promise<boolean> => {
    try {
      await routes.NOTIFICATIONS_READ
        .post<undefined, { affected: number }>(String(id))
        .notifyOnError(true)
        .send()
      void refreshUnreadCount()
      return true
    } catch {
      return false
    }
  }

  const markUnread = async (id: number): Promise<boolean> => {
    try {
      await routes.NOTIFICATIONS_UNREAD
        .post<undefined, { affected: number }>(String(id))
        .notifyOnError(true)
        .send()
      void refreshUnreadCount()
      return true
    } catch {
      return false
    }
  }

  /**
   * Bulk mark-read with optional non-Queryable filter scope (date
   * range / read state). Returns affected count.
   *
   * Note: the BE intentionally restricts the bulk-read scope to
   * non-Queryable filters only (V1 keeps destructive scope
   * predictable). For Queryable-narrowed bulk-read, operator uses
   * per-row buttons in the list.
   */
  const markAllRead = async (filters?: {
    from?: string | null
    to?: string | null
  }): Promise<number> => {
    try {
      const resp = await routes.NOTIFICATIONS_READ_ALL
        .post<typeof filters, { affected: number }>('')
        .notifyOnError(true)
        .body(filters ?? {})
        .send()
      void refreshUnreadCount()
      return resp?.affected ?? 0
    } catch {
      return 0
    }
  }

  /**
   * Bulk mark a SET of notifications (by id) read/unread — the inbox
   * selection-bar action. Recipient-scoped server-side (foreign ids
   * no-op). Returns affected count; refreshes the bell after.
   */
  const markManyRead = async (ids: number[]): Promise<number> => {
    if (ids.length === 0) return 0
    try {
      const resp = await routes.NOTIFICATIONS_BULK_READ
        .post<{ ids: number[] }, { affected: number }>()
        .notifyOnError(true)
        .body({ ids })
        .send()
      void refreshUnreadCount()
      return resp?.affected ?? 0
    } catch {
      return 0
    }
  }

  const markManyUnread = async (ids: number[]): Promise<number> => {
    if (ids.length === 0) return 0
    try {
      const resp = await routes.NOTIFICATIONS_BULK_UNREAD
        .post<{ ids: number[] }, { affected: number }>()
        .notifyOnError(true)
        .body({ ids })
        .send()
      void refreshUnreadCount()
      return resp?.affected ?? 0
    } catch {
      return 0
    }
  }

  /** Apply a local change without a BE round-trip — used after
   *  mark-read/unread to patch the list before the count refreshes. */
  const applyLocalReadChange = (id: number, list: Notification[], readAt: string | null): void => {
    const idx = list.findIndex((n) => n.id === id)
    if (idx === -1) return
    list[idx] = { ...list[idx], read_at: readAt }
  }

  /** Friendly display for the bell — caps at "99+". */
  const unreadBadgeLabel = computed<string | null>(() => {
    if (unreadCount.value === 0) return null
    if (unreadCount.value > 99) return '99+'
    return String(unreadCount.value)
  })

  return {
    unreadCount,
    loadingUnreadCount,
    unreadBadgeLabel,
    refreshUnreadCount,
    startPolling,
    stopPolling,
    markRead,
    markUnread,
    markManyRead,
    markManyUnread,
    markAllRead,
    applyLocalReadChange,
  }
})
